import { col, type Shape, WELL_RATE_LIMITS } from './base.ts';

export const LIFT_TYPE_ESP_LIMITS: Shape = [
  {
    canonical: 'fluidRate',
    members: { max: 'espMaxFluidRate', min: 'espMinFluidRate' },
    description: 'ESP fluid rate operating window — the max rate the ESP can handle and the min for stable operation.',
    kind: 'liquidRate',
  },
  col('maxMotorSpeedInductorType', 'unitless', 'Maximum ESP motor (pump) speed limit for an inductor-type motor.', {
    suffix: 'espMaxMotorSpeed_InductorType',
  }),
  col('maxMotorSpeedPmmType', 'unitless', 'Maximum ESP motor (pump) speed limit for a permanent-magnet (PMM) motor.', {
    suffix: 'espMaxMotorSpeed_PmmType',
  }),
  col('maxPowerAvailableGenerated', 'power', 'Maximum generated power available to the ESP.', {
    suffix: 'espMaxPwrAvail_Generated',
  }),
];

/**
 * Gas-lift operating limits on `LiftTypeParameter_String`: the gas-lift
 * pressure thresholds the artificial-lift agent limit views omit — casing-head
 * and manifold pressure ceilings and the minimum bottom-hole pressure target.
 * De-prefix axis is `none` (`prefix:''`); `suffix` stays verbatim while
 * `canonical` drops the `gl` marker.
 */
export const LIFT_TYPE_GAS_LIFT_LIMITS: Shape = [
  col('maxCasingHeadPressure', 'pressureAbsolute', 'Maximum allowable gas-lift casing head pressure.', {
    suffix: 'glMaxCasingHeadPressure',
  }),
  col('maxManifoldPressure', 'pressureAbsolute', 'Maximum gas-lift manifold pressure limit.', {
    suffix: 'glMaxManifoldPressure',
  }),
  col('minBHP', 'pressureAbsolute', 'Minimum gas-lift bottom-hole pressure target.', { suffix: 'glMinBHP' }),
];

/**
 * Rate / pressure limits on `LiftTypeParameter_String` carried un-prefixed on
 * the source: the wellhead pressure ceiling and the liquid/oil rate and GOR
 * caps. Choke size / choke-bean columns are UNITLESS for now (no unit slug);
 * here the source carries no choke-size scalar, so this group covers the rate
 * and pressure caps. De-prefix axis is `none` (`prefix:''`).
 */
export const LIFT_TYPE_RATE_LIMITS: Shape = [
  col('maxWellHeadPressure', 'pressureAbsolute', 'Maximum wellhead pressure limit.'),
  ...WELL_RATE_LIMITS,
];

/**
 * ESP active flags on `LiftTypeParameter_String`: the `_active` monitoring flag
 * for each limit in `LIFT_TYPE_ESP_LIMITS`. De-prefix axis is `none`
 * (`prefix:''`); `suffix` keeps the verbatim `_active` source column,
 * `canonical` drops the `esp` marker.
 */
export const LIFT_TYPE_ESP_ACTIVE_FLAGS: Shape = [
  col('maxFluidRate_active', 'liquidRate', 'Whether the ESP max fluid rate limit is actively monitored for alarms.', {
    suffix: 'espMaxFluidRate_active',
  }),
  col('minFluidRate_active', 'liquidRate', 'Whether the ESP min fluid rate limit is actively monitored for alarms.', {
    suffix: 'espMinFluidRate_active',
  }),
  col(
    'maxMotorSpeedInductorType_active',
    'unitless',
    'Whether the inductor-type motor (pump) speed limit is actively monitored for alarms.',
    { suffix: 'espMaxMotorSpeed_InductorType_active' },
  ),
  col(
    'maxMotorSpeedPmmType_active',
    'unitless',
    'Whether the PMM motor (pump) speed limit is actively monitored for alarms.',
    { suffix: 'espMaxMotorSpeed_PmmType_active' },
  ),
  col(
    'maxPowerAvailableGenerated_active',
    'power',
    'Whether the generated power-available ceiling is actively monitored for alarms.',
    { suffix: 'espMaxPwrAvail_Generated_active' },
  ),
];

/**
 * Gas-lift active flags on `LiftTypeParameter_String`: the `_active` monitoring
 * flag for each limit in `LIFT_TYPE_GAS_LIFT_LIMITS`. De-prefix axis is `none`
 * (`prefix:''`); `canonical` drops the `gl` marker.
 */
export const LIFT_TYPE_GAS_LIFT_ACTIVE_FLAGS: Shape = [
  col(
    'maxCasingHeadPressure_active',
    'pressureAbsolute',
    'Whether the gas-lift max casing head pressure limit is actively monitored for alarms.',
    { suffix: 'glMaxCasingHeadPressure_active' },
  ),
  col(
    'maxManifoldPressure_active',
    'pressureAbsolute',
    'Whether the gas-lift max manifold pressure limit is actively monitored for alarms.',
    { suffix: 'glMaxManifoldPressure_active' },
  ),
  col(
    'minBHP_active',
    'pressureAbsolute',
    'Whether the gas-lift min bottom-hole pressure target is actively monitored for alarms.',
    { suffix: 'glMinBHP_active' },
  ),
];

/**
 * Rate active flags on `LiftTypeParameter_String`: the `_active` monitoring flag
 * for each limit in `LIFT_TYPE_RATE_LIMITS`. De-prefix axis is `none`
 * (`prefix:''`); source columns are carried verbatim.
 */
export const LIFT_TYPE_RATE_ACTIVE_FLAGS: Shape = [
  col(
    'maxWellHeadPressure_active',
    'pressureAbsolute',
    'Whether the wellhead pressure limit is actively monitored for alarms.',
  ),
  col('maxLiquidRate_active', 'liquidRate', 'Whether the max liquid rate limit is actively monitored for alarms.'),
  col('maxOilRate_active', 'liquidRate', 'Whether the max oil rate limit is actively monitored for alarms.'),
  col('maxGOR_active', 'gasOilRatio', 'Whether the max gas-oil ratio limit is actively monitored for alarms.'),
];

/**
 * The full per-parameter active-flag set on `LiftTypeParameter_String`,
 * composed from the ESP / gas-lift / rate flag sub-shapes — the SSOT for each
 * group's flags lives in its sub-shape, this is the concatenation in
 * limit-group order.
 */
export const LIFT_TYPE_ACTIVE_FLAGS: Shape = [
  ...LIFT_TYPE_ESP_ACTIVE_FLAGS,
  ...LIFT_TYPE_GAS_LIFT_ACTIVE_FLAGS,
  ...LIFT_TYPE_RATE_ACTIVE_FLAGS,
];

/**
 * The network-grain relations carried by `JointOutputs_Network_Agent`:
 * the parent-`network` direct-relation (the NETWORK grain ref) plus the
 * junction/manifold entity ref. Both are kept verbatim (the source already
 * names them `network` / `jointEntity`), so the agent can scope by / join the
 * network. The source carries no column prefix, so `prefix:''` yields
 * `source === canonical` and no rename. NOTE the source's `entityName` is a
 * `z.json()` blob (NOT a string), which types to `unknownCol`, so it is
 * deliberately excluded here.
 */
